import { buildSiteTree, type CurrentUser, type Device, type Site, type SiteNode, type Tenant, type TenantRole, type Vehicle } from "@fts/core";
import { roleAtLeast } from "@fts/core";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { DataProvider, Member } from "../data";
import type { AuthlistStatus, OperatorData, SignInOptions } from "../data/provider";
import { effectiveRole } from "../lib/fleet";
import { releasePushOnSignOut } from "../push/client";

export type TenantData = {
  sites: Site[];
  vehicles: Vehicle[];
  /** Devices of the tenant visible to the user (RLS). */
  devices: Device[];
  /** null when the role may not list members. */
  members: Member[] | null;
  siteTree: SiteNode[];
  /** Sprint 5: operators, cards (safety+), documents (safety+), permissions. */
  operators: OperatorData;
  /** Sync state of the visible devices' authorisation lists. */
  authlist: AuthlistStatus[];
};

type Status = "loading" | "signedOut" | "ready";
type DataState = "idle" | "loading" | "ready" | "error";

export type SessionValue = {
  provider: DataProvider;
  status: Status;
  user: CurrentUser | null;
  tenants: Tenant[];
  tenant: Tenant | null;
  role: TenantRole | null;
  platformAdmin: boolean;
  data: TenantData | null;
  dataState: DataState;
  signIn(email: string, password: string, options?: SignInOptions): Promise<void>;
  /** E-mail code sign-in, step 2 (ADR 0020). Step 1 is provider.sendSignInCode. */
  signInWithCode(email: string, code: string, options?: Pick<SignInOptions, "remember">): Promise<void>;
  signInDemo(): Promise<void>;
  signOut(): Promise<void>;
  selectTenant(id: string): void;
  /** Full reload with a loading state (after errors). */
  reload(): void;
  /** Re-fetch the tenant data in place after a write; open dialogs stay mounted. */
  refresh(): Promise<void>;
  /** Re-fetch the user and tenant list (after accepting an invitation or creating a tenant). */
  refreshUser(selectTenantId?: string): Promise<void>;
};

const SessionContext = createContext<SessionValue | null>(null);
const TENANT_KEY = "fts.tenantId";

function readStoredTenant(): string | null {
  try {
    return localStorage.getItem(TENANT_KEY);
  } catch {
    return null;
  }
}

function storeTenant(id: string | null): void {
  try {
    if (id) localStorage.setItem(TENANT_KEY, id);
    else localStorage.removeItem(TENANT_KEY);
  } catch {
    // Non-critical.
  }
}

export function SessionProvider({ provider, children }: { provider: DataProvider; children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [data, setData] = useState<TenantData | null>(null);
  const [dataState, setDataState] = useState<DataState>("idle");
  const [reloadTick, setReloadTick] = useState(0);

  const enter = useCallback(
    async (u: CurrentUser) => {
      const list = await provider.listTenants();
      const stored = readStoredTenant();
      const initial = list.find((t) => t.id === stored) ?? list[0] ?? null;
      setUser(u);
      setTenants(list);
      setTenantId(initial?.id ?? null);
      setStatus("ready");
    },
    [provider],
  );

  useEffect(() => {
    let cancelled = false;
    provider
      .getCurrentUser()
      .then(async (u) => {
        if (cancelled) return;
        if (u) await enter(u);
        else setStatus("signedOut");
      })
      .catch(() => !cancelled && setStatus("signedOut"));
    return () => {
      cancelled = true;
    };
  }, [provider, enter]);

  const role = user && tenantId ? effectiveRole(user, tenantId) : null;

  const loadData = useCallback(
    async (tid: string, r: TenantRole): Promise<TenantData> => {
      const canListMembers = roleAtLeast(r, "admin");
      const [sites, vehicles, devices, members, operators, authlist] = await Promise.all([
        provider.listSites(tid),
        provider.listVehicles(tid),
        provider.listDevices(tid),
        canListMembers ? provider.listMembers(tid) : Promise.resolve(null),
        provider.listOperatorData(tid),
        provider.listAuthlistStatus(tid),
      ]);
      return { sites, vehicles, devices, members, siteTree: buildSiteTree(sites, vehicles), operators, authlist };
    },
    [provider],
  );
  // Ignore results of a load that was superseded by a tenant switch.
  const loadKey = useRef(0);

  useEffect(() => {
    if (status !== "ready" || !tenantId || !role) {
      setData(null);
      setDataState("idle");
      return;
    }
    let cancelled = false;
    const key = ++loadKey.current;
    setDataState("loading");
    loadData(tenantId, role)
      .then((d) => {
        if (cancelled || key !== loadKey.current) return;
        setData(d);
        setDataState("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
        setDataState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [loadData, status, tenantId, role, reloadTick]);

  const value = useMemo<SessionValue>(
    () => ({
      provider,
      status,
      user,
      tenants,
      tenant: tenants.find((t) => t.id === tenantId) ?? null,
      role,
      platformAdmin: !!user?.platformAdmin,
      data,
      dataState,
      async signIn(email, password, options) {
        const u = await provider.signIn(email, password, options);
        await enter(u);
      },
      async signInWithCode(email, code, options) {
        const u = await provider.verifySignInCode(email, code, options);
        await enter(u);
      },
      async signInDemo() {
        const u = await provider.signInDemo();
        await enter(u);
      },
      async signOut() {
        // Shared devices: stop this browser's alarm pushes for the user first (ADR 0019; errors ignored).
        await releasePushOnSignOut(provider);
        await provider.signOut();
        setUser(null);
        setTenants([]);
        setTenantId(null);
        setData(null);
        setStatus("signedOut");
      },
      selectTenant(id) {
        storeTenant(id);
        setTenantId(id);
      },
      reload() {
        setReloadTick((x) => x + 1);
      },
      async refresh() {
        if (!tenantId || !role) return;
        const key = loadKey.current;
        const d = await loadData(tenantId, role);
        if (key === loadKey.current) setData(d);
      },
      async refreshUser(selectTenantId) {
        const u = await provider.getCurrentUser();
        if (!u) {
          setStatus("signedOut");
          return;
        }
        const list = await provider.listTenants();
        setUser(u);
        setTenants(list);
        const next = list.find((t) => t.id === (selectTenantId ?? tenantId)) ?? list[0] ?? null;
        if (next) storeTenant(next.id);
        setTenantId(next?.id ?? null);
        setStatus("ready");
        // Same tenant id but new memberships: reload its data.
        if (next?.id === tenantId) setReloadTick((x) => x + 1);
      },
    }),
    [provider, status, user, tenants, tenantId, role, data, dataState, enter, loadData],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}
